import React, { EventHandler } from 'react';
import classNames from 'classnames';
import {
  LoadingSlugs,
  Card,
  FlexCard,
  Modal,
  TabbedContainer
} from '@codametrix/ui-components';
import {
  userUtils,
  cmxArtifacts,
  cmxDateTime,
  caseUtils,
  commonEnums,
  appServiceLines,
  AmplifyCore
} from '@codametrix/ui-common';
import {
  bpmStepsPopup,
  caseDetailPopup,
  caseDocumentsPopup,
  launchAmplifyPopup
} from './window-open-args';

// shared-views
import { CaseStatus } from '../case-status/case-status';
import { propAccessor } from '../case-prop-accessor/case-prop-accessor';
import { propAccessor2 } from '../case-prop-accessor/case-prop-accessor2';

import { Notes } from '../case-note/case-note';
// svgs
import * as dataURIs from './_data-uris';
import { DashboardActionBar } from '../case-action-bar/dashboard-action-bar';
import { IconHeader } from './icon-header';
import {
  PatientEncounterv2,
  CellMaker,
  CellPatientMaker
} from '../patient-encounter/patient-encounterv2';
import { Invoice } from '../transactions/invoice/invoice';

import {
  Conditional,
  DEFAULT_UNAVAILABLE_CAPABILITY
} from '@codametrix/ui-components';
import { BiomeIcon, IconName } from '@codametrix/verdigris';
import { NotesNavbarv2 } from '../notes-sidebar/notes-navbarv2';
import { Timeline } from '../timeline/timeline';
import { CMXIcon } from '@codametrix/ui-components';
import { InvoiceMetaHeading } from '../transactions/invoice/invoice-meta';
import {
  Button,
  VStack,
  HStack,
  Button as CKButton
} from '@chakra-ui/react';
import { ChakraProvider } from '@chakra-ui/react'; 
import theme from '../../theme';
import { invoiceAPInvoicesToInvoiceSources } from './invoice-sources/invoice-sources-transform';

const { CaseAccessState } = commonEnums;

const {
  artifactSets: { completeSet },
  isSingleSelection,
  getFeatureForSelection,
  isMultipleSelection
} = cmxArtifacts;

type OpenClaim = {
  fieldName: any;
  fieldIdentifier: any;
};

type CaseDetailState = {
  expandEncounter: boolean;
  openClaims: OpenClaim[];
  backHover: boolean;
  showAllArtifacts: boolean;
  expandDemographicsPanel: boolean;
  demographicsLocation: 'right' | 'bottom';
  userChangedLocation?: boolean;
};

const expandedProps = new Set([
  'procedure_type',
  'ordering_provider_name',
  'ordering_provider_number.number',
  'order_number.number',
  'date_of_service',
  'place_of_service',
  'tenant_name',
  'billing_provider_name',
  'billing_provider_number.number',
  'visit_number.number',
  'case_id'
]);
const expandedAmplifyProps = new Set([
  'accession',
  'exam_code',
  'exam_description',
  'reason_for_visit'
]);

const getStatusTimestamp = (
  caseRun: CMxAPI.CaseRun,
  stages: CMxCommonApp.TimelineStage[]
) => {
  if (caseRun.case_completed_datetime) {
    return cmxDateTime.format(
      caseRun.case_completed_datetime,
      cmxDateTime.FORMATS.DATE_TIME_WITH_ZONE
    );
  }

  if (stages.length) {
    const stage = [...stages].reverse().pop();
    return cmxDateTime.format(
      stage?.displayDate.raw ?? new Date(),
      cmxDateTime.FORMATS.DATE_TIME_WITH_ZONE
    );
  }

  return 'N/A';
};

const isCaseAvailable = (caseAccessState: string) => {
  return (
    caseAccessState === CaseAccessState.NONE ||
    caseAccessState === CaseAccessState.ACQUIRED
  );
};

const { conditionalUtils } = AmplifyCore;

class CaseDetailv2 extends React.Component<
  CMxCommonApp.CaseDetailProps,
  CaseDetailState
> {
  _artifactRef: React.RefObject<FlexCard>;
  _noteRef: React.RefObject<FlexCard>;
  _patientRef: React.RefObject<PatientEncounterv2>;
  noteRefs: { [docId: string]: React.RefObject<any> } = {};

  static defaultProps = {
    onCaseAction: (action: any) => {},
    onFeatureActivation: (selection: CMxCommonApp.ChargeSessionSelection) => {},
    showCoderNotesInfo: (coderNotesParams: CMxCommonApp.CoderNotesParams) => {},
    items: [],
    mode: commonEnums.CaseDetailMode.DASHBOARD,
    actionsState: {},
    capability: { on: DEFAULT_UNAVAILABLE_CAPABILITY },
    coderNotesCapability: {
      ...AmplifyCore.conditionalUtils.disabledCapability
    },
    noteUnavailableReason: {
      ...AmplifyCore.conditionalUtils.disabledCapability
    },
    flagList: [],
    activeVerticalMiniTab: commonEnums.MiniTabLabels.NOTES_LABEL,
    setActiveVerticalMiniTab: (activeTabName: string) => {},
    propertyConfig: {
      patient: [],
      summary: [],
      expanded: []
    },
    displayTopLeftPatientInfo: true,
    editable: false,
    caseAccessState: CaseAccessState.NONE,
    enableUserActivityComments:false
  };

  constructor(props: CMxCommonApp.CaseDetailProps) {
    super(props);
    this._handleToggleClick = this._handleToggleClick.bind(this);
    this._onFeatureSelection = this._onFeatureSelection.bind(this);
    this._handleOpenJson = this._handleOpenJson.bind(this);
    this._handleProcessIdChange = this._handleProcessIdChange.bind(this);
    this._handleGroupedCaseClick = this._handleGroupedCaseClick.bind(this);
    this._handlePatientExpansion = this._handlePatientExpansion.bind(this);
    this.onFeatureActivation = this.onFeatureActivation.bind(this);

    const showAllArtifacts = completeSet.size === props.artifactSet.size;

    this._artifactRef = React.createRef();
    this._noteRef = React.createRef();
    this._patientRef = React.createRef();

    this.props.setActiveNote(this.props.notes[0]);

    this.state = {
      expandEncounter: false,
      openClaims: [],
      backHover: false,
      showAllArtifacts: showAllArtifacts,
      expandDemographicsPanel: props.demographicsPanelVisibility === 'open' ||
                               props.demographicsPanelVisibility === undefined,
      demographicsLocation: props.demographicsPanelLocation || 'right'
    };
  }

  componentDidMount() {
    // CQA-UI: hide side nav bar
    this.props.hideSideNavBar();
    const { params = {} } = this.props.match ?? {};
    if (this.props.mode !== 'code-edit') {
      this.props.onNavigate(
        params.id,
        true,
        params.serviceLine,
        this.props.tenantId,
        this.props.hasAmplifyCaseRunV2Access
      );
    }
    return;
  }

  componentDidUpdate(prevProps: CMxCommonApp.CaseDetailProps) {
    this.props.notes.forEach((note) => {
      this.noteRefs[note.document_id] = React.createRef();
    });

    // Update demographics expansion when preference changes
    if (prevProps.demographicsPanelVisibility !== this.props.demographicsPanelVisibility) {
        this.setState({
          expandDemographicsPanel: this.props.demographicsPanelVisibility === 'open'
        });
      }
    }

  _handleGroupedCaseClick(ev: React.MouseEvent) {
    ev.preventDefault();
    const target =
      (ev.target as HTMLDivElement) || document.createElement('div');
    const linkable = target?.closest('a');
    if (!linkable) return;

    const item = linkable.attributes.getNamedItem('data-case-id');
    if (!item) return;

    const caseId = item.value;
    if (!caseId) return;
    // assume all grouped cases will be in the same service line
    const { params = {} } = this.props.match ?? {};
    this.props.onNavigate(
      caseId,
      undefined,
      params.serviceLine,
      this.props.tenantId,
      this.props.hasAmplifyCaseRunV2Access
    );
  }

  _onFeatureSelection(selection: CMxArtifacts.FeatureSelection) {
    if (selection.display) {
      if (isMultipleSelection(selection)) {
        const spanSelector = `.qa-${selection.code.uuid}`;
        const targetElement = document
          .querySelector(spanSelector)
          ?.closest('.accordion-section') as HTMLElement;

        if (targetElement) {
          this._artifactRef?.current?.scrollToElement(targetElement);
        }
      } else if (isSingleSelection(selection)) {
        const feature = getFeatureForSelection(selection);
        const inExpanded = expandedProps.has(feature.field);

        this._patientRef.current.expandEncounter(inExpanded);
        if (inExpanded) {
          const state = { ...this.state, expandEncounter: true };
          this.setState(state);
        } else if (feature.field === 'raw_body') {
          const {
            spans: [selectedSpan]
          } = feature;

          const spanSelector = `.span-${selectedSpan.uuid}`;
          const targetElement = document.querySelector(
            spanSelector
          ) as HTMLElement;

          if (targetElement) {
            this._noteRef?.current?.scrollToElement(targetElement);
          }
        }
      }
    }
    this.props.onFeatureSelection(selection);
  }

  onFeatureActivation(selection: CMxCommonApp.ChargeSessionSelection) {
    // only pop open the accordion for active selections
    if (!selection.selectionState.active && selection.evidence.length === 1) {
      const state = { ...this.state };
      const {
        evidence: [firstEvidence]
      } = selection;
      const claimMatcher = (claim) =>
        !(
          claim.fieldIdentifier === firstEvidence.fieldIdentifier &&
          claim.fieldName === firstEvidence.fieldName
        );
      state.openClaims = state.openClaims.filter(claimMatcher);
      state.expandEncounter = state.openClaims.length > 0;
      this.setState(state);
      this._patientRef.current.expandEncounter(state.expandEncounter);
    } else if (selection.selectionState.active) {
      const {
        evidence: [selectedSpan]
      } = selection;
      const { fieldName } = selectedSpan;

      if (fieldName === 'body') {
        const spanSelector = `.span-${selectedSpan.hash}`;
        const targetElement = document.querySelector(
          spanSelector
        ) as HTMLElement;

        if (targetElement) {
          this._noteRef?.current?.scrollToElement(targetElement);
        }
      } else {
        const inExpanded = new Set([
          ...expandedProps,
          ...expandedAmplifyProps
        ]).has(fieldName);
        if (inExpanded) {
          const state = { ...this.state, expandEncounter: true };
          state.openClaims = [
            ...state.openClaims,
            {
              fieldIdentifier: selectedSpan.fieldIdentifier,
              fieldName: selectedSpan.fieldName
            }
          ];
          this.setState(state);
          this._patientRef.current.expandEncounter(state.expandEncounter);
        }
      }
    }

    this.props.onFeatureActivation(selection);
  }

  _handlePatientExpansion(isOpen: boolean) {
    this.setState({
      ...this.state,
      expandEncounter: isOpen
    });
  }

  _handleToggleClick(event: React.MouseEvent) {
    event.preventDefault();
    const showAllArtifacts = !this.state.showAllArtifacts;
    this.props.toggleArtifacts(showAllArtifacts);
    this.setState({
      ...this.state,
      showAllArtifacts
    });
  }

  _handleOpenJson(event: React.MouseEvent) {
    event.preventDefault();

    const { activeItem } = this.props;
    const serviceLineConfig = appServiceLines.determineServiceLineConfig({
      name: activeItem.service_line,
      caseIdentifier: `${activeItem.case_uid || activeItem.case_id}`
    });
    const caseId =
      serviceLineConfig.caseIdProp in activeItem
        ? activeItem[serviceLineConfig.caseIdProp]
        : activeItem.case_id;
    const isStandalone =
      this.props.activeItem.case_type === commonEnums.UIModesEnum.STANDALONE;

    const params: any = { caseId };

    const target = event.currentTarget?.closest('.icon-button');
    if (target && this.props.activeItem.case_id) {
      const validTarget = target as HTMLAnchorElement;
      const href = `${validTarget?.dataset.href ?? ''}`;
      let base;

      params.processInstanceId = this.props.activeItem.process_instance_id;
      params.serviceLine = this.props.match?.params?.serviceLine ?? '';

      if (href.indexOf('import-amplify') !== -1) {
        this.props.onCaseAction({
          action: 'import-amplify',
          caseRun: this.props.activeItem,
          caseOrganizationContext: this.props.caseOrganizationContext
        });
      } else if (href.indexOf('launch-amplify') !== -1 && isStandalone) {
        // STANDALONE case same window
        const serviceLineConfig = appServiceLines.determineServiceLineConfig({
          name: this.props.activeItem.service_line,
          caseIdentifier: `${activeItem.case_uid || activeItem.case_id}`
        });

        const caseId =
          serviceLineConfig.caseIdProp in activeItem
            ? activeItem[serviceLineConfig.caseIdProp]
            : activeItem.case_id;

        if (this.props.loadAllPatientDetailOptions) {
          this.props.loadAllPatientDetailOptions(
            this.props.tenantId,
            this.props.activeItem.facility
          );
        }
        // CQA-UI: pass hasAmplifyCaseRunV2Access argument to openAmplifyCase
        this.props.openAmplifyCase({
          serviceLine: this.props.activeItem.service_line,
          caseId,
          id: this.props.activeItem.process_instance_guid,
          hasAmplifyCaseRunV2Access: this.props.hasAmplifyCaseRunV2Access
        });
      } else {
        //Dashboard Icon Button Menu
        params.managed = true;
        if (href.indexOf('bpm-steps') !== -1) {
          base = bpmStepsPopup;
          params.processInstanceId = this.props.activeItem.process_instance_id;
        } else if (href.indexOf('json-view') !== -1) {
          base = caseDetailPopup;
        } else if (href.indexOf('launch-amplify') !== -1) {
          // PLAYGROUND case new window

          base = launchAmplifyPopup;
          params.launchpoint = 'Automate';
          params.processInstanceGuid =
            this.props.activeItem.process_instance_guid;
          params.processInstanceGuids = this.props.items
            .map((item) => `${item.case_id}:${item.process_instance_guid}`)
            .join(',');
        } else if (href.indexOf('case-documents') != -1) {
          base = caseDocumentsPopup;
        }
        this.props.openChildWindow({ ...base, params });
      }
    }
  }

  _handleProcessIdChange(event: React.MouseEvent) {
    event.preventDefault();
    const target =
      (event.target as HTMLDivElement) || document.createElement('div');
    const linkable = target?.closest('a');
    if (!linkable) return;

    const item = linkable.attributes.getNamedItem('data-process-id');
    if (!item) return;

    const processId = parseInt(item.value);
    if (!processId) return;

    const caseRuns = Object.values(this.props.caseRuns).flat();

    const soughtRun = caseRuns.find(
      (caseRun) => caseRun.process_instance_id === processId
    );

    if (!soughtRun) return;
    this.props.setActiveProcess({
      caseRun: soughtRun,
      serviceLine: this.props.serviceLine
    });
  }

  handleDemographicsPanelClick(ev: React.MouseEvent) {
    ev.preventDefault();
    this.setState({
      expandDemographicsPanel: !this.state.expandDemographicsPanel
    });
  }

  handleDemographicsLocationClick(ev: React.MouseEvent, location) {
    ev.preventDefault();

    this.setState({
      demographicsLocation: location,
      userChangedLocation: true
    });
  }

  render() {
    const {
      props: {
        artifactLoadState,
        artifactSet,
        activeItem,
        codeEdits,
        loadState,
        notes,
        artifactList,
        codeDescriptions,
        formErrors,
        groupedCases,
        caseRuns,
        roles,
        stages,
        codeEditingPanel,
        propertyConfig,
        activations,
        simplifiedInvoices,
        caseEdits,
        organization,
        capability,
        mode,
        htmlNotes,
        noteUnavailableReason,
        documentMetadata,
        updateValue,
        updateCase,
        setActiveNote,
        processInstance,
        activeNote,
        flagList,
        coderNotesCapability,
        displayTopLeftPatientInfo,
        workItem,
        enableUserActivityComments
      }
    } = this;
    const onVerticalMiniTabSelection = (selection: CMxCommonApp.MiniTab) => {
      this.props.setActiveVerticalMiniTab(selection.displayName);
    };
    const onDocumentSelect = async (doc) => {
      const noteRef = this.noteRefs[doc.documentId];
      requestAnimationFrame(() =>
        noteRef?.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start', // Sets vertical alignment
          inline: 'nearest' //Sets horizontal alignment
        })
      );
      const newActiveNote = notes.find(
        (note) => note.document_id === doc.documentId
      );
      if (newActiveNote) {
        setActiveNote(newActiveNote);
      }
    };

    let {
      state: { expandEncounter, openClaims },
      props: { actionBar }
    } = this;
    // at the moment we only support a single case run
    const dataItem = activeItem;
    const { params = {} } = this.props.match ?? {};

    const prop = propAccessor2(dataItem, activations.property);

    var noteStyle;
    const showLoading = loadState.loading || loadState.initial;

    if (showLoading) {
      noteStyle = (
        <>
          <LoadingSlugs numberItems={30} />
        </>
      );
    } else {
      noteStyle = (
        <Notes
          notes={notes}
          noteRefs={this.noteRefs}
        />
      );
    }

    /** Start Patient details logic */
    const fieldEditorInfo = {
      caseEdits,
      organization,
      updateValue,
      updateCase
    };

    const cellMaker = CellMaker(
      prop,
      this.props.activeItem,
      fieldEditorInfo,
      null
    );
    const cellPatientMaker = CellPatientMaker(prop, this.props.activeItem);

    // all info less last item
    let patientConfigDetails = this.props.propertyConfig?.patient?.slice(
      0,
      this.props.propertyConfig?.patient?.length - 1
    );

    const patientLastItem = this.props.propertyConfig.patient.slice(-1);

    // add hr less the last one item
    patientConfigDetails = patientConfigDetails.map((patient, index) => {
      return {
        ...patient,
        addHr: index + 1 !== patientConfigDetails.length
      };
    });

    const topCells = patientConfigDetails.map(cellPatientMaker);
    const topRightCell = patientLastItem.map(cellMaker);

    /** End Patient details logic */

    const statusTimestamp = getStatusTimestamp(dataItem, stages);

    const loadingClasses = [{ 'loading-card': showLoading }];
    const rawArtifactLength = dataItem.artifacts?.length ?? 0;
    const displayedArtifactLength = artifactList.length;
    const userIsCoder = userUtils.isCoder(roles);
    const codeEditMode = this.props.mode === commonEnums.CaseDetailMode.EDIT;

    let multipleArtifacts = false;

    if (!userIsCoder) {
      if (simplifiedInvoices && simplifiedInvoices.length) {
        multipleArtifacts = simplifiedInvoices.length > 0;
      } else {
        multipleArtifacts =
          rawArtifactLength > displayedArtifactLength || rawArtifactLength > 0;
      }
    }

    const caseDetailClasses = [
      'case-detailv2',
      'simple-layout',
      `demographics-${this.state.demographicsLocation}`,
      `case-${this.props.mode}-mode`,
      {
        'multiple-artifacts': multipleArtifacts,
        'encounter-expanded': expandEncounter,
        'case-detail-loading': showLoading,
        'demographics-open': this.state.expandDemographicsPanel,
        'demographics-closed': !this.state.expandDemographicsPanel
      }
    ];

    //History of code automation
    const artifactListClasses = ['automation-container', ...loadingClasses];
    const artifactToggle = (
      <div className="info-cell">
        codes &nbsp;
        <span className="artifact-toggle">
          {userIsCoder ? (
            <span />
          ) : (
            <span>
              (
              <a
                className="qa-artifact-toggle"
                onClick={this._handleToggleClick}
                href="#toggle-artifacts">
                {this.state.showAllArtifacts ? `show summary` : `show all`}
              </a>
              )
            </span>
          )}
        </span>
      </div>
    );

    const preGoLive = caseUtils.isPreGoLive(activeItem);
    const mrn = `${prop('patient_identifier.issuer', 'SRC N/A')}: ${prop(
      'patient_identifier.number'
    )}`;
    const accessionNumber = `${prop(
      'primary_clinical_identifier.number'
    )} src: (${prop('primary_clinical_identifier.issuer')})`;

    const providerComments: string[] = Array.from(
      new Set( // get unique comments with a set
        this.props?.activeItem?.invoices
          ?.find(
            (invoice) =>
              invoice.type === commonEnums.CaseArtifactTypes.ASSEMBLED
          ) // just the ASSEMBLED invoice
          ?.transactions.map(
            (transaction) => transaction?.custom_values?.comment
          ) // get comments for all transactions
          .filter(Boolean) // remove undefined/null comments
      )
    );

    const encounterHeader = (
      <span className="encounter-expanders">
        patient encounter
        <span className="show-toggle">
          (
          <a
            onClick={(ev) => {
              ev.preventDefault();

              if (expandEncounter) {
                openClaims = [];
              } else {
                openClaims = [
                  ...openClaims,
                  { fieldIdentifier: 'showToggle', fieldName: 'showToggle' }
                ];
              }

              this.setState({
                expandEncounter: !expandEncounter,
                openClaims
              });
            }}
            className="qa-expand-encounter"
            href="#/show-details">
            show {expandEncounter ? 'summary' : 'more'}
          </a>
          )
        </span>
      </span>
    );

    if (this.props.mode === 'dashboard' && !actionBar) {
      actionBar = (
        <DashboardActionBar
          actionsState={this.props.actionsState}
          caseRun={this.props.activeItem}
          handleOpenJson={this._handleOpenJson}
          handleProcessIdChange={this._handleProcessIdChange}
          handleBackClick={this.props.handleBackClick}
          caseRuns={caseRuns}
        />
      );
    }

    const charges = Array.isArray(simplifiedInvoices)
      ? simplifiedInvoices.map((invoice, idx) => {
          return (
            <div key={idx} className="invoice-item-group">
              {invoice.meta ? (
                <InvoiceMetaHeading
                  meta={invoice.meta}
                  onImportClick={this._handleGroupedCaseClick}
                />
              ) : (
                <></>
              )}
              <Invoice
                panelActivations={activations.panel}
                onFeatureSelection={this.onFeatureActivation}
                simplifiedInvoice={invoice}
                editable={false}
                transactionV2={conditionalUtils.capabilityValue(
                  this.props.capabilities?.capabilities?.transactionV2?.on
                )}
              />
            </div>
          );
        })
      : false;

    const shouldShowAddAttachDocsButton =
      conditionalUtils.capabilityValue(
        this?.props?.capabilities?.capabilities?.attachDocs?.on
      ) &&
      this.props.UIView === commonEnums.UIViewEnum.AMPLIFY &&
      this.props.UIMode === commonEnums.UIModesEnum.STANDALONE &&
      appServiceLines.isIPC(activeItem.service_line) &&
      this.props.editable;

    const showImprovementsCapability =
      codeEditMode &&
      conditionalUtils.capabilityValue(
        this.props.capabilities?.capabilities?.improvements?.on
      );

    const tabsItems = [
      {
        id: 'Notes',
        displayName: 'Notes',
        active:
          this.props.activeVerticalMiniTab ===
          commonEnums.MiniTabLabels.NOTES_LABEL,
        body: (
          <>
            <div className="provider-comments-section">
              {providerComments.length > 0 &&
                providerComments.map((comment, index) => (
                  <div
                    className="provider-comments"
                    key={`provider-comment-${index}`}>
                    <div className="provider-comments-icon">
                      <BiomeIcon
                        iconName={IconName.Comments}
                        titleText="Provider Comments"
                      />
                    </div>
                    <p className="provider-text">{comment}</p>
                  </div>
                ))}
            </div>

            <div className="doc-nav-wrap">
              <div className="doc-nav">
                <NotesNavbarv2
                  onSelect={onDocumentSelect}
                  activeNote={activeNote}
                  documentMetadata={documentMetadata}
                  isIPC={appServiceLines.isIPC(activeItem.service_line)}
                  capabilities={this.props.capabilities.capabilities}
                  showAddAttachDocsButton={shouldShowAddAttachDocsButton}
                  showAttachDocsModal={() =>
                    this.props.showAttachDocsModal(
                      activeItem as CMxAPI.CaseRunV2
                    )
                  }
                />
              </div>

              <div className="documentation-section">{noteStyle}</div>
            </div>
          </>
        ),
        titleText: 'Notes',
        styleName: 'tab-teal'
      },
      displayTopLeftPatientInfo && {
        id: 'relatedCases',
        displayName: commonEnums.MiniTabLabels.RELATED_LABEL,
        active:
          this.props.activeVerticalMiniTab ===
          commonEnums.MiniTabLabels.RELATED_LABEL,
        body: this.props.relatedCasesView,
        titleText: commonEnums.MiniTabLabels.RELATED_DESCRIPTION,
        styleName: 'tab-green'
      },
      showImprovementsCapability && {
        id: 'improvements',
        displayName: commonEnums.MiniTabLabels.IMPROVEMENTS_LABEL,
        active:
          this.props.activeVerticalMiniTab ===
          commonEnums.MiniTabLabels.IMPROVEMENTS_LABEL,
        body: this.props.documentImprovementsPanel,
        titleText: commonEnums.MiniTabLabels.IMPROVEMENTS_DESCRIPTION,
        styleName: 'tab-yellow'
      },
      {
        id: 'activity',
        displayName: commonEnums.MiniTabLabels.ACTIVITY_LABEL,
        active:
          this.props.activeVerticalMiniTab ===
          commonEnums.MiniTabLabels.ACTIVITY_LABEL,
        body: <Timeline items={this.props.activity} enableUserActivityComments={enableUserActivityComments} editable={this.props.editable}/>,
        titleText: commonEnums.MiniTabLabels.ACTIVITY_DESCRIPTION,
        styleName: 'tab-orange'
      }
    ].filter(Boolean);

    if (
      conditionalUtils.capabilityValue(
        this.props.capabilities?.capabilities?.codeSourcesFeatures?.on
      ) &&
      mode === 'code-edit'
    ) {
      tabsItems.push({
        id: 'Code Sources',
        displayName: commonEnums.MiniTabLabels.CODE_SOURCES,
        active:
          this.props.activeVerticalMiniTab ===
          commonEnums.MiniTabLabels.CODE_SOURCES,
        body: invoiceAPInvoicesToInvoiceSources(
          this.props?.sourceInvoices?.sourceInvoices
        ),
        titleText: commonEnums.MiniTabLabels.CODE_SOURCES_DESCRIPTION,
        styleName: 'tab-tropical-50'
      });
    }

    const toggleButton = (
      <CKButton
        className="demographics-toggle-button"
        variant={'toggle'}
        onClick={(ev) => this.handleDemographicsPanelClick(ev)}
        data-dd-action-name={
          !this.state.expandDemographicsPanel
            ? '~Open Demographics V2'
            : '~Close Demographics V2'
        }>
        {!this.state.expandDemographicsPanel ? (
          <BiomeIcon
            iconName={IconName.Chevron_Left}
            titleText="Show Demographics"
          />
        ) : (
          <BiomeIcon
            iconName={IconName.Chevron_Right}
            titleText="Collapse Demographics"
          />
        )}
      </CKButton>
    );

    const placementToggle = (
      <>
        {this.state.demographicsLocation == 'bottom' ? (
          <Button
            className="location-toggle-button"
            variant={'toggle'}
            onClick={(ev) => this.handleDemographicsLocationClick(ev, 'right')}
            data-dd-action-name={'~Pin Demographics to Right'}>
            <BiomeIcon
              iconName={IconName.Arrow_Right}
              titleText="Pin to Right"
              iconSize="12"
            />
          </Button>
        ) : (
          <Button
            className="location-toggle-button"
            variant={'toggle'}
            onClick={(ev) => this.handleDemographicsLocationClick(ev, 'bottom')}
            data-dd-action-name={'~Pin Demographics to Bottom'}>
            <BiomeIcon
              iconName={IconName.Arrow_Right}
              titleText="Pin to Bottom"
              iconSize="12"
            />
          </Button>
        )}
      </>
    );

    const toggleButtonGroup = (
      <>
        {this.state.demographicsLocation == 'bottom' ? (
          <HStack className="location-toggle-button-group">
            {toggleButton}
            {placementToggle}
          </HStack>
        ) : (
          <VStack className="location-toggle-button-group">
            {toggleButton}
            {placementToggle}
          </VStack>
        )}
      </>
    );

    return (
      <ChakraProvider theme={theme}>
        <div className={classNames(caseDetailClasses)}>
          {actionBar}

          {formErrors?.status === 'PAGE NOT FOUND' ? (
            <Modal
              title={'Unable to load case (Case id# ' + params.id + ')'}
              buttonText={'Return'}
              closable={false}
              icon={<img src={dataURIs.AlertIcon} width={30} height={30} />}
              onSubmit={this.props.handleBackClick as EventHandler<any>}
              children={
                <p>
                  {
                    'The case you are trying to view does not exist. Please check the information and try again.'
                  }
                </p>
              }
            />
          ) : null}
          {/*CQA-UI: removed patient demographics section  */}
          {/* left column */}
          <div className="left-column">
            {/* patient information*/}

            {/*BENCH LOADING WITH SLUGS*/}

            {/*This content shows the patient information on the coding screen.*/}
            {displayTopLeftPatientInfo && (
              <div className="patient-top-info-container">
                <div
                  className="patient-info-primary sensitive-data"
                  data-testid="patient-info-name">
                  {prop('patient_name')}
                  <div className="patient-info-secondary">{topCells}</div>
                </div>
                {topRightCell}
                <a
                  href={'#/patient-info'}
                  className="timeline-button-container"
                  onClick={(ev) => {
                    ev.preventDefault();
                    this.props.showPatientInfo(
                      this.props.activeItem as CMxAPI.CaseRunV2
                    );
                  }}>
                  <CMXIcon
                    svgIconClass="patient-timeline"
                    svgIconName="BiomeCalender"
                    svgIconSize="24"
                    svgTitleText="Patient Timeline"
                  />
                </a>
              </div>
            )}

            {/* note */}

            <TabbedContainer
              items={tabsItems}
              onSelection={onVerticalMiniTabSelection}
            />
          </div>

          {/* middle column  */}
          <div className="middle-column">
            {!codeEditMode && (
              <div className="large-status-container">
                {showLoading ? (
                  <Card
                    className={classNames([
                      'status-loading',
                      ...loadingClasses
                    ])}>
                    <LoadingSlugs numberItems={2} />
                  </Card>
                ) : (
                  <CaseStatus
                    onClick={this._handleGroupedCaseClick}
                    billingGroupLoadState={this.props.billingGroupLoadState}
                    groupedCases={groupedCases}
                    caseRun={activeItem}
                    timeStamp={statusTimestamp}
                    isPreGoLive={preGoLive}
                  />
                )}
              </div>
            )}
            {/* right hand panel */}
            {codeEditingPanel || (
              <FlexCard
                unflexed={true}
                //@ts-ignore
                ref={this._artifactRef}
                header={
                  artifactLoadState.initial ? (
                    <span />
                  ) : (
                    <IconHeader
                      svgIconName={IconName.Coding}
                      svgIconClass="card-header-icon"
                      header={artifactToggle}
                      svgTitleText="Coding"
                    />
                  )
                }
                className={classNames(artifactListClasses)}>
                <div className="case-status-codes">
                  <div className="prediction-section">
                    {artifactLoadState.initial ? (
                      <LoadingSlugs numberItems={2} />
                    ) : (artifactList || []).length === 0 && !charges ? (
                      <h4 className="content-vacant">
                        {' '}
                        {userIsCoder ||
                        (activeItem.artifacts || []).length === 0
                          ? 'No codes available'
                          : 'No codes displayed'}
                      </h4>
                    ) : charges ? (
                      <>{charges}</>
                    ) : (
                      <></>
                    )}
                  </div>
                </div>
              </FlexCard>
            )}
          </div>
          {/*CQA-UI: added new section for patient demographics section */}
          {/* right column  */}
          <div className="right-column">
            {/* patient details */}
            {showLoading ? (
              <>
                <LoadingSlugs numberItems={3} />
                <br />
              </>
            ) : (
              <>
                {toggleButtonGroup}
                <PatientEncounterv2
                  //@ts-ignore
                  ref={this._patientRef}
                  activations={activations}
                  propertyConfig={propertyConfig}
                  activeItem={activeItem}
                  handleExpansion={this._handlePatientExpansion}
                  showCoderNotesInfo={this.props.showCoderNotesInfo}
                  showProviderInfo={this.props.showProviderInfo}
                  fieldEditorInfo={{
                    caseEdits,
                    organization,
                    updateValue,
                    updateCase
                  }}
                  capability={{
                    ...capability,
                    on:
                      //@ts-ignore
                      conditionalUtils.capabilityValue(capability.on) &&
                      mode === commonEnums.CaseDetailMode.EDIT &&
                      isCaseAvailable(this.props.caseAccessState)
                  }}
                  coderNotesCapability={coderNotesCapability}
                  flagList={flagList}
                  workItem={workItem}
                  activeContext={this.props.context?.activeContext}
                />
              </>
            )}
          </div>
        </div>
      </ChakraProvider>
    );
  }
}

export { CaseDetailv2 };
